import { useEffect, useState } from 'react';
import api, { ApiError } from '../../../lib/apiClient';
import { useAuth } from '../../../auth/useAuth';
import { useToast } from '../../../context/ToastContext';

interface Commentary {
  period_type: 'quarterly' | 'annual';
  period_label: string;
  commentary: string;
}

// Current-quarter and current-year labels, e.g. "2026-Q1" / "2026" — the
// commentary an officer is most likely writing right now. Older periods
// remain editable by re-visiting this component with a different label if
// ever needed; no period picker for now (see the module plan's cut list).
function currentPeriods(): { quarterly: string; annual: string } {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return { quarterly: `${now.getFullYear()}-Q${quarter}`, annual: `${now.getFullYear()}` };
}

// Quarterly/annual trend commentary — a free-text narrative alongside the
// Asset Allocation report explaining what's driving the fund's allocation
// trend this period. Not tied to any other single report (see
// InvestmentReportCommentaryController's own comment).
export function ReportCommentary() {
  const { can } = useAuth();
  const { showError, showSuccess } = useToast();
  const canWrite = can('investments.update');
  const periods = currentPeriods();
  const [periodType, setPeriodType] = useState<'quarterly' | 'annual'>('quarterly');
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<Commentary | null>(null);
  const [editing, setEditing] = useState(false);

  const periodLabel = periods[periodType];

  useEffect(() => {
    api
      .get<Commentary[]>('/investment-report-commentaries', { params: { period_type: periodType } })
      .then((res) => {
        const match = res.data.find((c) => c.period_label === periodLabel) ?? null;
        setSaved(match);
        setText(match?.commentary ?? '');
        setEditing(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const save = async () => {
    try {
      const res = await api.put<Commentary>(
        `/investment-report-commentaries/${periodType}/${encodeURIComponent(periodLabel)}`,
        { commentary: text },
      );
      setSaved(res.data);
      setEditing(false);
      showSuccess('Commentary saved.');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to save commentary.');
    }
  };

  return (
    <div className="card">
      <div className="changelog-release-header">
        <h2>Trend Commentary</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className={`btn ${periodType === 'quarterly' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPeriodType('quarterly')}
          >
            Quarterly ({periods.quarterly})
          </button>
          <button
            type="button"
            className={`btn ${periodType === 'annual' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setPeriodType('annual')}
          >
            Annual ({periods.annual})
          </button>
        </div>
      </div>

      {editing ? (
        <div className="form-grid">
          <label className="span-2">
            Commentary for {periodLabel}
            <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          <div className="form-actions span-2">
            <button type="button" className="btn btn-ghost" onClick={() => { setText(saved?.commentary ?? ''); setEditing(false); }}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className={saved ? '' : 'muted'}>{saved?.commentary || `No commentary recorded for ${periodLabel} yet.`}</p>
          {canWrite && (
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)}>
              {saved ? 'Edit' : 'Add Commentary'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
